import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../utils/api';
import { Spinner, Container, Card, Button, Form, Alert } from 'react-bootstrap';

const ResetPassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false); // State for showing passwords
  const history = useHistory();

  // Create a ref for the first input field
  const currentPasswordRef = useRef(null);

  // Focus the first input field when the component mounts
  useEffect(() => {
    currentPasswordRef.current.focus();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    if (newPassword.length < 10) {
      setError('Password must be at least 10 characters long.');
      return;
    }

    // Current and new password cannot be the same
    if (currentPassword === newPassword) {
      setError('New password cannot be the same as the current password.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.post('/api/reset-password', {
        currentPassword,
        newPassword,
      });

      setSuccessMessage(response.data.message);
      setError(null);

      setTimeout(() => {
        history.push('/login');
      }, 2000);
    } catch (error) {
      console.error('Error resetting password:', error);

      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to reset password. Please check your current password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPasswords(!showPasswords);
  };

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Header>
              <h3 className="text-center">Reset Password</h3>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {successMessage && <Alert variant="success">{successMessage} Redirecting to login page...</Alert>}
              <Form onSubmit={handleResetPassword}>
                <Form.Group controlId="currentPassword" className="mb-3">
                  <Form.Label>Current Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    ref={currentPasswordRef}
                    placeholder="Enter your current password"
                  />
                </Form.Group>
                <Form.Group controlId="newPassword" className="mb-3">
                  <Form.Label>New Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Enter your new password"
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 10 characters long.
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId="confirmPassword" className="mb-3">
                  <Form.Label>Confirm New Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Confirm your new password"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox" 
                    label="Show Passwords"
                    checked={showPasswords}
                    onChange={togglePasswordVisibility}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
                  {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Reset Password'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default ResetPassword;